import { ReactComponent as ArrowLeftGlyph } from '@/assets/images/svg/glyphs/arrow-left.svg';
import Link from '@/components/Link';
import { MAX_CONTAINER_WIDTH } from '@/components/layout/Container';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import { marginX, mediaBreakpointUp } from '@/styles/layout/utils';
import { remCalc } from '@/styles/typography/utils';
import React, { FC, PropsWithChildren } from 'react';
import styled from 'styled-components';

// #region - Components

const PageWrapper = styled.div`
  max-width: calc(${remCalc(MAX_CONTAINER_WIDTH)} + var(--breakpoint-padding-x));
  ${marginX('auto')}
  margin-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
`;

interface HeaderProps {
  backgroundColor: string;
}

const Header = styled.header<HeaderProps>`
  position: relative;
  z-index: 1;

  padding-left: calc(var(--breakpoint-padding-x) / 2);
  padding-right: calc(var(--breakpoint-padding-x) / 2);
  background-color: ${(props) => props.backgroundColor};
  padding-top: ${remCalc(24)};
  padding-bottom: ${remCalc(100)};

  display: flex;
  flex-direction: column;

  ${mediaBreakpointUp('tablet')} {
    display: grid;
    grid-template-columns: auto 40%;
    grid-template-rows: auto minmax(0, 1fr);
    grid-row-gap: 0;
    grid-column-gap: ${remCalc(32)};

    padding-top: ${remCalc(72)};
    padding-bottom: ${remCalc(140)};
  }

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: auto 45%;
    border-radius: ${remCalc(10)};
  }
`;

const BackButton = styled.a`
  border-radius: ${remCalc(4)};
  width: ${remCalc(24)};
  height: ${remCalc(24)};
  display: block;

  transition: background-color 0.2s ease-in-out;

  &:hover,
  &:active {
    background-color: ${hexToRGBA(COLORS.BLACK, 0.1)};
  }

  ${mediaBreakpointUp('desktop')} {
    width: ${remCalc(32)};
    height: ${remCalc(32)};
  }
`;

const HeaderTopBar = styled.div`
  display: grid;
  grid-template-columns: ${remCalc(40)} auto;
  align-items: center;

  ${mediaBreakpointUp('tablet')} {
    grid-row: 1;
    grid-column: 1;
  }

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: ${remCalc(64)} auto;
  }
`;

const HeaderLeftColumn = styled.div`
  position: relative;
  padding-left: ${remCalc(40)};

  ${mediaBreakpointUp('tablet')} {
    grid-row: 2;
    grid-column: 1;
  }

  ${mediaBreakpointUp('desktop')} {
    padding-left: ${remCalc(64)};
  }

  ${BackButton} {
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const HeaderRightColumn = styled.div`
  ${mediaBreakpointUp('tablet')} {
    grid-row: 1 / span 2;
    grid-column: 2;
  }
`;

const MainContent = styled.section`
  position: relative;
  z-index: 2;

  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10, 10, 0, 0)};

  margin-top: ${remCalc(-76)};
  min-height: ${remCalc(76)};

  padding-top: ${remCalc(24)};
  padding-bottom: ${remCalc(76)};
  padding-left: calc(var(--breakpoint-padding-x) / 2);
  padding-right: calc(var(--breakpoint-padding-x) / 2);

  margin-left: calc(var(--breakpoint-padding-x) / 2);
  margin-right: calc(var(--breakpoint-padding-x) / 2);

  ${mediaBreakpointUp('tablet')} {
    padding-left: calc(var(--breakpoint-padding-x) / 2);
    padding-right: calc(var(--breakpoint-padding-x) / 2);
  }

  ${mediaBreakpointUp('desktop')} {
    padding-top: calc(var(--breakpoint-padding-x) / 2);
  }
`;

// #endregion - Components

interface ResourceLayoutProps extends PropsWithChildren {
  color?: string | null;
  backButton?: {
    href: string;
    title?: string;
    onClick?: () => void;
  };
  headerTopBar?: React.ReactChild | null;
  headerLeftColumn?: React.ReactChild | null;
  headerRightColumn?: React.ReactChild | null;
  content?: React.ReactChild | null;
}

const ResourceLayout: FC<ResourceLayoutProps> = ({ children, ...props }) => {
  const backButton = props.backButton && (
    <Link href={props.backButton.href}>
      <BackButton title={props.backButton.title} onClick={props.backButton.onClick}>
        <ArrowLeftGlyph />
      </BackButton>
    </Link>
  );

  return (
    <PageWrapper>
      <Header backgroundColor={props.color ?? COLORS.VIOLET.HIGHLIGHT}>
        {!!props.headerTopBar && (
          <HeaderTopBar>
            <div>{backButton}</div>
            <div>{props.headerTopBar}</div>
          </HeaderTopBar>
        )}

        <HeaderLeftColumn>
          {!props.headerTopBar && backButton}
          <div>{props.headerLeftColumn}</div>
        </HeaderLeftColumn>

        <HeaderRightColumn>{props.headerRightColumn}</HeaderRightColumn>
      </Header>

      <MainContent>{children}</MainContent>
    </PageWrapper>
  );
};

export default ResourceLayout;
